import { Location } from 'history';
import { BAZAAR_OF_username, BAZAAR_SEO_BABY, CONFIRM } from 'mk/autogenerated/translations/HerBazaarPage.e74aead03c562e0eebcf84ca1e92c6ae'
import { hasAds } from 'mk/bazaar/common/userUtils';
import { Filter } from 'mk/bazaar/product/filter/filterUtils';
import { analyticsStoreEvent } from 'mk2/actions';
import { bazaarNavigationActivate, bazaarNavigationStoreBack } from 'mk2/apps/bazaar/actions';
import { FacetsData } from 'mk2/apps/bazaar/components/FacetsFilter';
import HerBazaar from 'mk2/apps/bazaar/containers/HerBazaar/HerBazaar';
import { herBazaarLoad, Checked } from 'mk2/apps/bazaar/containers/HerBazaar/HerBazaar.actions';
import reducer from 'mk2/apps/bazaar/containers/HerBazaar/HerBazaar.reducers';
import saga from 'mk2/apps/bazaar/containers/HerBazaar/HerBazaar.sagas';
import { getHerBazaarSellerFilterState } from 'mk2/apps/bazaar/containers/HerBazaar/HerBazaar.selectors';
import { NavigationHistoryState } from 'mk2/apps/bazaar/helpers';
import { deserializeFilter } from 'mk2/apps/bazaar/helpers/filter';
import navigationReducer, { ScrollPosition } from 'mk2/apps/bazaar/reducers/navigation';
import navigationSaga from 'mk2/apps/bazaar/sagas/navigation';
import {
    ComplaintsPerProductSchema,
    ProductEntity,
    ProductSchema,
    SellerEntity,
    SellerSchema,
} from 'mk2/apps/bazaar/schemas';
import { getBazaarNavigationState } from 'mk2/apps/bazaar/selectors/navigation';
import { breadcrumbsSeller } from 'mk2/apps/bazaar/structuredData';
import { bazaarGridUrl, bazaarSellerBazaarUrl } from 'mk2/apps/bazaar/urls';
import { Btn } from 'mk2/components/Btn';
import { NotFound } from 'mk2/components/NotFound';
import { AppName } from 'mk2/containers/Application/Application';
import { ComplaintEntity, ComplaintSchema } from 'mk2/containers/Complaint/Complaint.schemas';
import { cacheLast } from 'mk2/helpers/cache';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { url } from 'mk2/helpers/urls';
import Page, { AnchorLeaderboardProps, BrandingProps } from 'mk2/pages/Page';
import {
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestSettings,
    getRequestUser,
    AppState,
} from 'mk2/reducers';
import { LoadingState } from 'mk2/reducers/loadingState';
import { MenuSelection, UserEntity } from 'mk2/schemas';
import { findDenormalizedEntityByCond, getDenormalizedEntities } from 'mk2/selectors';
import { interpolate } from 'mk2/services/i18n';
import { Store } from 'mk2/store/configureStore';
import { denormalize } from 'normalizr';
import { parse, stringify } from 'query-string';
import React from 'react';
import { MapStateToPropsParam } from 'react-redux';
import { RouteComponentProps, StaticContext } from 'react-router';

export interface OwnProps {}

export interface StateProps {
    requestUser: UserEntity;
    requestUserPermissions: string[];
    seller: SellerEntity;
    loadingState: LoadingState;
    errorMessage: string;
    filterFromRoute: Filter;
    hits: number;
    facets: FacetsData;
    products: ProductEntity[];
    isMobile: boolean;
    baseUrl: string;
    complaintsPerProduct: {
        [productId: number]: ComplaintEntity[];
    };
    checked: Checked;
    scroll: ScrollPosition;
    openedFacet: string;
}

export interface DispatchProps {
    onLoad(username: string, filter: Filter);
    onStoreEvent(name: string, props: any);
    onNavigationStoreBack(back: Location, scroll: ScrollPosition);
    onNavigationActivate(hash: string);
}

export type RouteProps = RouteComponentProps<
    {
        username: string;
        category?: string;
        filters?: string;
        page?: string;
    },
    StaticContext,
    NavigationHistoryState
>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class HerBazaarPage extends Page<OwnProps, StateProps, DispatchProps> {
    private filterFromRouteCache = cacheLast(true);
    private complaintsPerProductCache = cacheLast(true);

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const {
                match: {
                    params: { username },
                },
            } = ownProps;
            const qp = parse(ownProps.location.search) || {};
            const openedFacet = qp.opened as string;

            const seller = findDenormalizedEntityByCond<SellerEntity>(
                state,
                SellerSchema,
                `cacheKey-HerBazaarPage-${username}-seller`,
                (sel) => sel.username === username,
            );

            const requestUserPermissions = getRequestPermissions(state);

            const settings = getRequestSettings(state);

            const filterFromRoute = this.filterFromRouteCache(
                () =>
                    deserializeFilter(
                        ownProps.match.params,
                        ownProps.location.search,
                        {
                            gridSort: settings.gridSort,
                        },
                        requestUserPermissions,
                    ),
                // Key is empty for first location on server
                ownProps.location.key || `${ownProps.location.pathname}${ownProps.location.search}`,
                settings.gridSort,
                requestUserPermissions,
            );

            const filterState = getHerBazaarSellerFilterState(state, username, filterFromRoute);

            const complaintsPerProduct = this.complaintsPerProductCache(
                () =>
                    state.entities[ComplaintSchema.key]
                        ? denormalize(filterState.complaintsPerProductIds, ComplaintsPerProductSchema, state.entities)
                        : {},
                filterState.complaintsPerProductIds,
            );
            const navigationState = getBazaarNavigationState(state);

            return {
                requestUser: getRequestUser(state),
                requestUserPermissions,
                seller,
                loadingState: filterState.loadingState,
                errorMessage: filterState.errorMessage,
                facets: filterState.facets,
                hits: filterState.hits,
                filterFromRoute,
                products: getDenormalizedEntities<ProductEntity>(state, ProductSchema, filterState.productsIds),
                isMobile: getRequestDeviceMobile(state),
                baseUrl: getRequestBaseUrl(state),
                checked: filterState.checked,
                complaintsPerProduct,
                scroll: navigationState.scroll,
                openedFacet,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: herBazaarLoad,
            onStoreEvent: analyticsStoreEvent,
            onNavigationStoreBack: bazaarNavigationStoreBack,
            onNavigationActivate: bazaarNavigationActivate,
        };
    }

    public getMetaTitle(props: Props): string {
        const title = interpolate(BAZAAR_OF_username, {
            username: props.match.params.username,
        });
        return `${title} | ${BAZAAR_SEO_BABY}`;
    }

    public render(props: Props): false | JSX.Element {
        // Filter cannot be parsed from URL
        if (!props.filterFromRoute) {
            return (
                <NotFound
                    match={props.match}
                    location={props.location}
                    history={props.history}
                    staticContext={props.staticContext}
                    backUrl={url(bazaarGridUrl, {})}
                />
            );
        }

        return <HerBazaar {...props} />;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return !!props.openedFacet && props.isMobile;
    }

    public getDefaultBackPath(props: Props): string | null {
        return url(bazaarSellerBazaarUrl, { username: props.match.params.username });
    }

    public getHeaderRight(props: Props): React.ReactElement {
        // No button for cats select
        if (props.isMobile && props.openedFacet === 'cats') {
            return null;
        } else if (props.isMobile && props.openedFacet) {
            const qs = parse(props.location.search) || {};
            const { opened, ...restQs } = qs;
            return (
                <Btn
                    label={CONFIRM}
                    data-cy="confirm-button"
                    link={{
                        ...props.location,
                        state: {
                            dontScrollOnPush: true,
                        },
                        search: stringify(restQs),
                    }}
                />
            );
        } else {
            return null;
        }
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: app,
            activeItem: null,
        };
    }

    public getMetaRobots(props: Props): string {
        if (props.location.search !== '') {
            return 'noindex,follow';
        }

        if (props.seller && props.seller.hasBlockedBazaar) {
            return 'noindex,nofollow';
        }

        return super.getMetaRobots(props);
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer('containers.bazaar.navigation', navigationReducer);
        store.injectSaga('containers.bazaar.navigation', navigationSaga);
        store.injectReducer('containers.bazaar.herBazaar', reducer);
        store.injectSaga('containers.bazaar.herBazaar', saga);
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: 'Market Section - Branding',
        };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { products, requestUser, isMobile } = props;

        if (!isMobile || requestUser && !hasAds(requestUser)) {
            return null;
        }

        if ((products?.length || 0) < 10) {
            return null;
        }

        return {
            zone: 'Market Section - Anchor Ad Leaderboard (Mobile)',
        };
    }

    public getStructuredData(props: Props): object[] {
        return [breadcrumbsSeller(props.baseUrl, props.seller)];
    }
}
